import {Component, DestroyRef, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {Catalog} from 'app/Model/Catalog/Catalog';
import {ImportContainer} from 'app/Model/dataProcess/importContainer';
import {CatalogService} from 'app/Services/CatalogManagement/catalog.service';
import {Router} from "@angular/router";
import DevExpress from "devextreme";
import {DxContextMenuComponent} from "devextreme-angular";
import {ImportContainerService} from "../../../../Services/DataProcess/import-container.service";
import {custom} from "devextreme/ui/dialog";
import {TranslateService} from "@ngx-translate/core";
import {JobService} from "../../../../Services/job.service";
import {LoginService} from "../../../../Services/login.service";
import {StartAction} from "../../../../Model/Dto/StartAction";
import {SystemService} from "../../../../Services/system.service";
import {SignalrService} from "../../../../Services/signalr.service";
import {Message} from "../../../../Model/System/Message";
import {BackgroundJobEvent} from "../../../../Model/Dto/BackgroundJobEvent";
import ContextMenuItem = DevExpress.ui.dxFileManager.ContextMenuItem;
import {OAuthService} from "angular-oauth2-oidc";
import {HttpClient} from "@angular/common/http";
import {firstValueFrom} from "rxjs";
import {
  UserActionWindowComponent
} from "../../../../catalog/functions/userAction/user-action-window/user-action-window.component";

@Component({
  selector: 'np-catalog-tab',
  templateUrl: './catalog-tab.component.html',
  styleUrls: ['./catalog-tab.component.css'],
  standalone: false
})
export class CatalogTabComponent implements OnInit, OnChanges {
  @Input() container: ImportContainer;
  @Input() initialSelectedCatalogId: string | null = null;
  @ViewChild('catalogContextMenu', {static: false}) catalogContextMenu: DxContextMenuComponent;
  @ViewChild('compareExportWindow', {static: false}) compareExportWindow: UserActionWindowComponent;
  @Output() inspectCatalog = new EventEmitter<{ container: ImportContainer; catalogId: string }>();
  @Output() openImportPopupRequested = new EventEmitter<void>();

  catalogs: Catalog[] = [];

  compareCatalogId: string;
  compareCandidates: Catalog[] = [];
  isDragOver: boolean = false;
  isDragOverCompare: boolean = false;
  archiveCatalogs: { id: string; name: string }[] = [];
  activeCatalogItems: { id: string; name: string }[] = [];
  compareCatalogItems: { id: string; name: string }[] = [];
  isArchiveLoading: boolean = false;
  selectedCatalogId: string;
  importMimeVisible: boolean = false;
  isTemplate: boolean = false;
  contextMenuItems: ContextMenuItem[];

  isComparing: boolean = false;
  comparisonCounts: { neu: number; geaendert: number; geloescht: number };
  isDeletingCatalog: boolean = false;
  isUploading: boolean = false;
  private readonly destroyRef = inject(DestroyRef);

  private readonly uploadUrl = 'api/import/ImportCatalogChunky';
  private readonly chunkSize = 10_000_000;
  private readonly supportedExtensions = ['.xml', '.zip', '.xlsx', '.xls', '.csv'];

  constructor(
    private catalogService: CatalogService,
    private router: Router,
    public importContainerService: ImportContainerService,
    public translate: TranslateService,
    private jobService: JobService,
    public loginService: LoginService,
    private systemService: SystemService,
    private signalrService: SignalrService,
    private oAuthService: OAuthService,
    private http: HttpClient
  ) {
    this.contextMenuItems = [
      {
        id: 0,
        text: this.translate.instant("Katalog anzeigen..."),
        icon: 'glyphicon glyphicon-eye-open',
        beginGroup: true,
        opensNewTab: true,
      },
      {
        id: 1,
        text: this.translate.instant("Medien importieren..."),
        icon: 'glyphicon glyphicon-copy',
        beginGroup: true
      },
      {
        id: 2,
        text: this.translate.instant("Löschen..."),
        icon: 'glyphicon glyphicon-trash',
        beginGroup: true
      },
    ]
  }

  async ngOnInit(): Promise<void> {
    if (this.hasContainerCatalogIds()) {
      await this.loadArchiveCatalogNames();
    }

    this.catalogService.catalogNameUpdated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ id, name }) => {
        this.updateLocalCatalogName(id, name);
      });

    this.signalrService.refreshCatalogList$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(async () => {
      if (!this.isDeletingCatalog && this.container?.id) {
        const customerId = this.catalogService.loginService.currentUser.customerId;
        const result = await this.importContainerService.get(this.container.id, customerId);
        if (result) {
          this.container = result;
          if (this.hasContainerCatalogIds()) {
            await this.loadArchiveCatalogNames();
          }
        }
      }
    });
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['container']) {
      if (this.hasContainerCatalogIds()) {
        await this.loadArchiveCatalogNames();
      } else {
        this.archiveCatalogs = [];
        this.activeCatalogItems = [];
        this.compareCatalogItems = [];
        this.selectedCatalogId = null;
      }
    }
  }
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(): void {
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;

    const droppedFiles: File[] = Array.from(event.dataTransfer?.files ?? []);
    void this.handleSelectedFiles(droppedFiles, false);
  }

  async onDropAddTop(event: any): Promise<void> {
    const fromData = event.fromData;
    const fromIndex = event.fromIndex;

    if (!fromData || !Array.isArray(fromData) || !this.container) {
      return;
    }

    const droppedCatalog = fromData[fromIndex];
    if (!droppedCatalog?.id) {
      return;
    }

    if (this.activeCatalogItems.length > 0) {
      const prev = this.activeCatalogItems[0];
      if (prev.id !== droppedCatalog.id) {
        this.archiveCatalogs = [prev, ...this.archiveCatalogs];
      }
    }

    this.container.activeCatalogId = droppedCatalog.id;
    this.activeCatalogItems = [{ id: droppedCatalog.id, name: droppedCatalog.name }];

    this.archiveCatalogs = this.archiveCatalogs.filter(c => c.id !== droppedCatalog.id);
    if (this.compareCatalogItems.length > 0 && this.compareCatalogItems[0].id === droppedCatalog.id) {
      this.compareCatalogItems = [];
      this.container.compareCatalogId = null;
    }

    this.comparisonCounts = null;
    await this.persistDropZones();
  }

  onDragOverCompare(event: DragEvent): void {
    event.preventDefault();
    this.isDragOverCompare = true;
  }

  onDragLeaveCompare(): void {
    this.isDragOverCompare = false;
  }

  onDropCompare(event: DragEvent): void {
    event.preventDefault();
    this.isDragOverCompare = false;

    const droppedFiles: File[] = Array.from(event.dataTransfer?.files ?? []);
    void this.handleSelectedFiles(droppedFiles, true);
  }

  async onDropAddCompare(event: any): Promise<void> {
    const fromData = event.fromData;
    const fromIndex = event.fromIndex;

    if (!fromData || !Array.isArray(fromData) || !this.container) {
      return;
    }

    const droppedCatalog = fromData[fromIndex];
    if (!droppedCatalog?.id) {
      return;
    }

    if (this.compareCatalogItems.length > 0) {
      const prev = this.compareCatalogItems[0];
      if (prev.id !== droppedCatalog.id) {
        this.archiveCatalogs = [prev, ...this.archiveCatalogs];
      }
    }

    this.container.compareCatalogId = droppedCatalog.id;
    this.compareCatalogItems = [{ id: droppedCatalog.id, name: droppedCatalog.name }];

    this.archiveCatalogs = this.archiveCatalogs.filter(c => c.id !== droppedCatalog.id);
    if (this.activeCatalogItems.length > 0 && this.activeCatalogItems[0].id === droppedCatalog.id) {
      this.activeCatalogItems = [];
      this.container.activeCatalogId = null;
    }

    this.comparisonCounts = null;
    await this.persistDropZones();
  }

  get canStartComparison(): boolean {
    return this.activeCatalogItems.length > 0 && this.compareCatalogItems.length > 0;
  }

  onStartComparison(): void {
    if (!this.canStartComparison || !this.container) {
      return;
    }

    this.isComparing = true;
    this.comparisonCounts = null;

    const startAction = new StartAction();
    startAction.customerId = this.loginService.currentUser.customerId;
    startAction.catalogId = this.container.activeCatalogId;
    startAction.actionId = `CompareCatalog(${this.container.compareCatalogId}:false:writeToSource)`;

    this.jobService.startUserAction(startAction).subscribe({
      next: (jobId) => {
        this.systemService.notify(new Message(this.translate.instant('Vergleich wurde gestartet.')));
        if (jobId) {
          this.subscribeToComparisonResult(jobId);
        } else {
          this.isComparing = false;
        }
      },
      error: () => {
        this.systemService.notify(new Message(this.translate.instant('Vergleich konnte nicht gestartet werden.')));
        this.isComparing = false;
      }
    });
  }

  get showCompareExport(): boolean {
    return this.loginService.hasAddonBauvista;
  }

  onStartCompareExport(): void {
    if (!this.canStartComparison || !this.container) {
      return;
    }

    this.compareExportWindow.showWindow(
      'CompareCatalogExcelExport',
      JSON.stringify({
        sourceCompareCatalog: this.container.compareCatalogId,
        sourceCompareCatalogName: this.compareCatalogItems[0].name
      })
    );
  }

  private async subscribeToComparisonResult(jobId: number): Promise<void> {
    const jobIdString = jobId.toString();
    await this.signalrService.startFinishedJobFeed(jobIdString);

    this.signalrService.finishedJob$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(({jobId: finishedId}) => {
      if (finishedId === jobIdString) {
        this.jobService.getJobEvents(jobId).subscribe({
          next: (events) => {
            const counts = this.parseComparisonEvents(events);
            this.comparisonCounts = counts;
            this.isComparing = false;
            this.signalrService.stopFinishedJobFeed(jobIdString);
          },
          error: () => {
            this.isComparing = false;
            this.signalrService.stopFinishedJobFeed(jobIdString);
          }
        });
      }
    });
  }

  private parseComparisonEvents(events: BackgroundJobEvent[]): { neu: number; geaendert: number; geloescht: number } | null {
    let neu: number;
    let geaendert: number;
    let geloescht: number;

    for (const event of events) {
      if (event.parameter?.startsWith('new:')) {
        neu = parseInt(event.parameter.split(':')[1], 10);
      } else if (event.parameter?.startsWith('changed:')) {
        geaendert = parseInt(event.parameter.split(':')[1], 10);
      } else if (event.parameter?.startsWith('deleted:')) {
        geloescht = parseInt(event.parameter.split(':')[1], 10);
      }
    }

    if (neu !== null && geaendert !== null && geloescht !== null) {
      return { neu, geaendert, geloescht };
    }
    return null;
  }

  onFileInputChange(event: Event): void {
    const fileInputElement = event.target as HTMLInputElement;
    const selectedFiles: File[] = Array.from(fileInputElement.files ?? []);
    void this.handleSelectedFiles(selectedFiles, false);
    fileInputElement.value = '';
  }

  onFileInputChangeCompare(event: Event): void {
    const fileInputElement = event.target as HTMLInputElement;
    const selectedFiles: File[] = Array.from(fileInputElement.files ?? []);
    void this.handleSelectedFiles(selectedFiles, true);
    fileInputElement.value = '';
  }

  private async handleSelectedFiles(selectedFiles: File[], isCompare: boolean): Promise<void> {
    if (selectedFiles.length === 0 || !this.container?.id) {
      return;
    }

    const validFiles: File[] = [];
    for (const file of selectedFiles) {
      const ext = '.' + (file.name.split('.').pop() ?? '').toLowerCase();
      if (this.supportedExtensions.includes(ext)) {
        validFiles.push(file);
      } else {
        this.systemService.notify(new Message(
          this.translate.instant('Nicht unterstütztes Format: {{name}}. Erlaubt sind XML, ZIP, XLSX, XLS, CSV.', { name: file.name }),
          'error'
        ));
      }
    }

    if (validFiles.length === 0) {
      return;
    }

    this.isUploading = true;
    const customerId = this.loginService.currentUser.customerId;
    const token = this.oAuthService.getAccessToken();

    try {
      for (const file of validFiles) {
        const fileGuid = crypto.randomUUID();
        const totalChunks = Math.ceil(file.size / this.chunkSize);

        for (let index = 0; index < totalChunks; index++) {
          const start = index * this.chunkSize;
          const end = Math.min(start + this.chunkSize, file.size);

          const chunkMetadata = JSON.stringify({
            FileGuid: fileGuid,
            FileName: file.name,
            Index: index,
            TotalCount: totalChunks,
            FileSize: file.size,
            FileType: file.type
          });

          const formData = new FormData();
          formData.append('file', file.slice(start, end), file.name);
          formData.append('chunkMetadata', chunkMetadata);

          await firstValueFrom(
            this.http.post(this.uploadUrl, formData, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'CustomerId': String(customerId),
                'ContainerId': this.container.id,
                'compareCatalog': String(isCompare)
              },
              responseType: 'text'
            })
          );
        }
      }

      await this.reloadContainerAndCatalogs();
      this.catalogService.viewService.jobsVisible = true;
      this.systemService.notify(new Message(this.translate.instant('Katalog hochgeladen')));
    } catch {
      this.systemService.notify(new Message(this.translate.instant('Fehler beim Hochladen'), 'error'));
    } finally {
      this.isUploading = false;
    }
  }

  private async reloadContainerAndCatalogs(): Promise<void> {
    if (!this.container?.id) {
      return;
    }
    const customerId = this.loginService.currentUser.customerId;
    const result = await this.importContainerService.get(this.container.id, customerId);
    if (result) {
      this.container = result;
    }
    if (this.hasContainerCatalogIds()) {
      await this.loadArchiveCatalogNames();
    }
  }

  get hasCatalogs(): boolean {
    return (this.container?.catalogIds?.length ?? 0) > 0;
  }

  onCatalogOverflowClicked(catalogId: string | null | undefined, event: any): void {
    if (!catalogId || !this.container) {
      return;
    }
    this.catalogContextMenu.instance.option('target', event.event.currentTarget);
    this.catalogContextMenu.instance.option('position', {
      my: 'left top',
      at: 'left bottom',
      offset: '0 5'
    });
    this.catalogContextMenu.instance.show();
    this.selectCatalog(catalogId);
    this.inspectCatalog.emit({
      container: this.container,
      catalogId
    });
  }

  async itemClick(event: any): Promise<void> {
    switch (event.itemIndex) {
      case 0:
        const urlTree = this.router.createUrlTree([
          '/catalog',
          this.catalogService.loginService.currentUser.customerId,
          this.selectedCatalogId,
          'info'
        ]);
        const urlString = this.router.serializeUrl(urlTree);
        window.open(urlString, '_blank');
        break;
      case 1:
        this.importMimeVisible = true;
        break;
      case 2:
        this.showDeleteDialog(this.selectedCatalogId, this.catalogService.loginService.currentUser.customerId);
        break;
    }
  }

  hideImportMime() {
    this.importMimeVisible = false;
  }

  showDeleteDialog(deletedCatalogId: string, customerId: string) {
    let normalCatalogDialog = custom({
      showTitle: true,
      title: this.translate.instant("Katalog löschen"),
      dragEnabled: false,
      messageHtml: this.translate.instant("Wollen Sie den Katalog wirklich löschen?"),
      buttons: [
        {
          text: this.translate.instant("Ja"),
          onClick: async (e) => {
            await this.onDeleteClicked(deletedCatalogId, customerId);
          }
        },
        {
          text: this.translate.instant("Nein"),
        }
      ]
    });
    normalCatalogDialog.show()
  }

  async onDeleteClicked(catalogId: string, customerId: string) {
    this.archiveCatalogs = this.archiveCatalogs.filter(c => c.id !== catalogId);
    this.activeCatalogItems = this.activeCatalogItems.filter(c => c.id !== catalogId);
    this.compareCatalogItems = this.compareCatalogItems.filter(c => c.id !== catalogId);
    if (this.container.activeCatalogId === catalogId) {
      this.container.activeCatalogId = null;
    }
    if (this.container.compareCatalogId === catalogId) {
      this.container.compareCatalogId = null;
    }
    if (this.container.catalogIds) {
      this.container.catalogIds = this.container.catalogIds.filter(id => id !== catalogId);
    }
    this.selectedCatalogId = null;
    this.comparisonCounts = null;

    this.isDeletingCatalog = true;

    try {
      await Promise.all([
        this.importContainerService.deleteContainerCatalogsByCatalogId(catalogId, customerId),
        this.catalogService.remCatalogAsync(catalogId)
      ]);

      const result = await this.importContainerService.get(this.container.id, customerId);
      if (result) {
        this.container = result;
      }
    } finally {
      this.isDeletingCatalog = false;
    }
  }

  onCatalogJumpClicked(catalogId: string | null | undefined): void {
    if (!catalogId) {
      return;
    }

    const customerId = this.catalogService.loginService.currentUser.customerId;
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/catalog/' + customerId + '/' + catalogId + '/info'])
    );
    window.open(url, '_blank');
  }

  onCatalogRowClick(catalogId: string | null | undefined): void {
    if (!catalogId || !this.container) {
      return;
    }

    this.selectCatalog(catalogId);
    this.inspectCatalog.emit({
      container: this.container,
      catalogId
    });
  }

  async onDropAddArchive(event: any): Promise<void> {
    const fromData = event.fromData;
    const fromIndex = event.fromIndex;

    if (!fromData || !Array.isArray(fromData) || !this.container) {
      return;
    }

    const droppedCatalog = fromData[fromIndex];
    if (!droppedCatalog?.id) {
      return;
    }

    const toIndex = event.toIndex ?? 0;
    const updated = [...this.archiveCatalogs];
    updated.splice(toIndex, 0, { id: droppedCatalog.id, name: droppedCatalog.name });
    this.archiveCatalogs = updated;

    if (this.activeCatalogItems.some(c => c.id === droppedCatalog.id)) {
      this.activeCatalogItems = [];
      this.container.activeCatalogId = null;
    }
    if (this.compareCatalogItems.some(c => c.id === droppedCatalog.id)) {
      this.compareCatalogItems = [];
      this.container.compareCatalogId = null;
    }

    this.comparisonCounts = null;
    await this.persistDropZones();
  }

  onArchiveReorder(event: any): void {
    const fromIndex = event.fromIndex;
    const toIndex = event.toIndex;

    if (fromIndex === toIndex) {
      return;
    }

    const updated = [...this.archiveCatalogs];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, moved);
    this.archiveCatalogs = updated;
  }

  private updateLocalCatalogName(catalogId: string, name: string): void {
    const active = this.activeCatalogItems.find(c => c.id === catalogId);
    if (active) active.name = name;

    const compare = this.compareCatalogItems.find(c => c.id === catalogId);
    if (compare) compare.name = name;

    const archive = this.archiveCatalogs.find(c => c.id === catalogId);
    if (archive) archive.name = name;
  }

  private selectCatalog(catalogId: string): void {
    this.selectedCatalogId = catalogId;
  }

  private hasContainerCatalogIds(): boolean {
    return !!this.container?.catalogIds && this.container.catalogIds.length > 0;
  }

  private async persistDropZones(): Promise<void> {
    if (!this.container?.id) {
      return;
    }

    const customerId = this.catalogService.loginService.currentUser.customerId;
    try {
      await this.importContainerService.updateDropZones(
        this.container.id,
        this.container.activeCatalogId ?? null,
        this.container.compareCatalogId ?? null,
        customerId
      );
    } catch (err) {
      console.error('Fehler beim Speichern der Drop-Zonen', err);
    }
  }

  private async loadArchiveCatalogNames(): Promise<void> {
    this.isArchiveLoading = true;
    this.archiveCatalogs = [];
    this.activeCatalogItems = [];
    this.compareCatalogItems = [];
    this.selectedCatalogId = null;

    try {
      const customerId = this.catalogService.loginService.currentUser.customerId;
      const catalogIds = this.container?.catalogIds ?? [];
      const uniqueIds = [...new Set(catalogIds)].filter(id => !!id);

      if (uniqueIds.length === 0) {
        return;
      }

      await this.catalogService.setCatalogs(customerId);

      const sortedIds = [...uniqueIds].reverse();

      const catalogInfos = await Promise.all(
        sortedIds.map(async id => {
          const fromList = this.catalogService.catalogs?.find(c => c.id === id);
          const name = fromList?.catalogName
            ?? await this.catalogService.getCatalogName(id, customerId);
          return { id, name };
        })
      );

      const activeId = this.container?.activeCatalogId ?? null;
      const compareId = this.container?.compareCatalogId ?? null;

      const activeCatalog = activeId ? catalogInfos.find(c => c.id === activeId) : undefined;
      this.activeCatalogItems = activeCatalog?.name ? [{ id: activeId, name: activeCatalog.name }] : [];

      const compareCatalog = compareId ? catalogInfos.find(c => c.id === compareId) : undefined;
      this.compareCatalogItems = compareCatalog?.name ? [{ id: compareId, name: compareCatalog.name }] : [];

      this.archiveCatalogs = catalogInfos
        .filter(c => c.id !== activeId && c.id !== compareId && !!c.name)
        .map(c => ({
          id: c.id,
          name: c.name as string
        }));

      this.autoSelectCatalog(activeId);
    } finally {
      this.isArchiveLoading = false;
    }
  }

  private autoSelectCatalog(activeId: string | null): void {
    if (!this.container) {
      return;
    }

    const allIds = [
      ...this.activeCatalogItems.map(c => c.id),
      ...this.compareCatalogItems.map(c => c.id),
      ...this.archiveCatalogs.map(c => c.id)
    ];

    if (allIds.length === 0) {
      return;
    }

    let targetId: string | null = null;
    if (this.initialSelectedCatalogId && allIds.includes(this.initialSelectedCatalogId)) {
      targetId = this.initialSelectedCatalogId;
    } else if (activeId && allIds.includes(activeId)) {
      targetId = activeId;
    } else {
      targetId = allIds[0];
    }

    if (targetId) {
      this.selectedCatalogId = targetId;
      this.inspectCatalog.emit({ container: this.container, catalogId: targetId });
    }
  }
}
